import { useState } from "react";
import {
  documentsymbolgreen,
  greyuploadicon,
  uploadbigicon,
} from "../svgs";

import ToastMessage from "../../../../../utils/mui/righttoptoast";
import { Backdrop, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import openEmrinstance from "../../../../../api/emrapiInstance";

/* ------------------ CONSTANTS ------------------ */
const DOCUMENT_TYPES = ["Imaging", "Labs", "Clinical", "Encounter"];

const categoryid = {
  Imaging: 32,
  Labs: 2,
  Clinical: 13,
  Encounter: 33,
};

const subviews = {
  Imaging: 1,
  Labs: 0,
  Clinical: 3,
  Encounter: 2,
};

const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const MAX_FILE_SIZE_MB = 10;

/* ------------------ COMPONENT ------------------ */
const UploadDocument = ({ open, data, setsubview, onClose, onUploadComplete, }) => {
  const [files, setFiles] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [search, setSearch] = useState("");
  const [customType, setCustomType] = useState("");
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);
const isExternalUpload = Boolean(onUploadComplete);

  const patientid = data?.patientid;
  const apikey = process.env.REACT_APP_OPENEMR_KEY;

  if (!open) return null;

  /* ------------------ HELPERS ------------------ */
  const getTodayDate = () => dayjs().format("YYYY-MM-DD");

  const generateFileName = (category, file) => {
    const timestamp = dayjs().valueOf();
    return `${category}_${timestamp}_${file.name}`;
  };

  const handleToastClose = () => setOpenToast(false);

  const resetForm = () => {
    setFiles([]);
    setSelectedType("");
    setSearch("");
    setCustomType("");
    setToastMessage("");
    setOpenToast(false);
  };

  /* ------------------ SEARCH ------------------ */
  const handleSearch = (value) => {
    setSearch(value);
    const match = DOCUMENT_TYPES.find(
      (t) => t.toLowerCase() === value.toLowerCase()
    );
    if (match) setSelectedType(match);
  };

  /* ------------------ FILE HANDLER ------------------ */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const validFiles = [];

    for (const file of selectedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setToastMessage("Only PDF, PNG, JPG files are allowed");
        setToastSeverity("error");
        setOpenToast(true);
        continue;
      }

    if (file.size > 25 * 1024 * 1024) {
  setToastMessage("Each file must be under 25MB");
  setToastSeverity("error");
  setOpenToast(true);
  continue;
}


      validFiles.push(file);
    }

    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isUploadDisabled = !files.length || !selectedType || loading;

  /* ------------------ SUBMIT ------------------ */
  const submit = async () => {
    try {
      setLoading(true);

      for (const file of files) {
        const formData = new FormData();
        formData.append("apikey", apikey);
        formData.append("pid", patientid);
        formData.append("file", file);
        formData.append("fname", generateFileName(selectedType, file));
        formData.append("category_id", categoryid[selectedType]);
        formData.append(
          "document_note",
          customType || `${selectedType} document`
        );
        formData.append("docdate", getTodayDate());

        await openEmrinstance.post("/documents/create/?emr", formData);
      }
if (onUploadComplete) {
  onUploadComplete(files);
}
  if (onUploadComplete) {

  setToastMessage("Documents uploaded successfully");
      setToastSeverity("success");
      setOpenToast(true);
  }
    

      setTimeout(() => {
        setLoading(false);
        resetForm();
        onClose();
        setsubview(subviews[selectedType]);
      }, 1200);
    } catch (error) {
      console.error(error);
      setLoading(false);
      setToastMessage("Failed to upload documents");
      setToastSeverity("error");
      setOpenToast(true);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

  <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
<div className="flex min-h-full items-start justify-center py-10">
        <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-5 py-4">
            <div className="text-lg font-semibold flex items-center gap-2">
              <span>{greyuploadicon}</span>
              Upload Document
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="px-5 py-4">
            {/* Upload Area */}
            <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-[2px] border-[#E5E7EB] p-6 text-center">
              <input
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                onChange={handleFileChange}
              />

              {!files.length ? (
                <>
                  <div className="mb-2 bg-[#F3F4F6] rounded-full h-12 w-12 flex items-center justify-center">
                    {uploadbigicon}
                  </div>
                  <p className="text-sm font-medium">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, PNG, JPG up to 25MB
                  </p>
                </>
              ) : (
                <>
                  <div className="mb-2 bg-[#D0FAE5] rounded-full h-10 w-10 flex items-center justify-center">
                    {documentsymbolgreen}
                  </div>
<div className="mt-1 w-full max-h-40 overflow-y-auto pr-1">
  {files.map((file, index) => (
    <div
      key={index}
      className="mb-2 flex items-center justify-between rounded-md border p-2 text-left"
    >
      <div className="truncate">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <p className="text-xs text-gray-500">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>

      <button
        type="button"
        onClick={() => removeFile(index)}
        className="ml-2 text-xs text-red-500 hover:underline"
      >
        Remove
      </button>
    </div>
  ))}
</div>

                </>
              )}
            </label>

            {/* Document Type */}
            <p className="mt-4 mb-2 text-sm font-semibold text-start">
              Document Type
            </p>

            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full mb-3 rounded-full bg-[#F5F6FA] border border-[#D5D5D5] px-4 py-2 text-sm"
            />

            <div className="mb-3 grid grid-cols-2 gap-2">
              {DOCUMENT_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    selectedType === type
                      ? "bg-blue-50 border-blue-500 text-blue-600"
                      : "border-gray-300"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder="Specify document type"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              className="w-full rounded-xl border px-3 py-2 text-sm"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-5 py-3">
            <button
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm"
            >
              Cancel
            </button>

            <button
              disabled={isUploadDisabled}
              onClick={submit}
              className={`rounded-md px-4 py-2 text-sm text-white ${
                isUploadDisabled
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              Upload Document
            </button>
          </div>
        </div>
</div>
        <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
      </div>
    </>
  );
};

export default UploadDocument;
